/**
 * CategoryMotif — small decorative SVG glyphs that sit in the corner of each
 * Reading Room category tile and the intro card. Not photographic — simple
 * line illustrations that hint at the motif without competing with the room.
 *
 * Each glyph uses `currentColor` so it inherits the active category accent.
 */

import type { CategoryMeta } from './categories';

export type MotifKind = CategoryMeta['motifKind'];

export interface CategoryMotifProps {
  kind: MotifKind;
  size?: number;
  className?: string | undefined;
}

export function CategoryMotif({ kind, size = 84, className }: CategoryMotifProps) {
  const cls = className ?? '';
  switch (kind) {
    case 'vinyl':
      return <VinylMotif size={size} className={cls} />;
    case 'cassette':
      return <CassetteMotif size={size} className={cls} />;
    case 'atlas':
      return <AtlasMotif size={size} className={cls} />;
    case 'film-poster':
      return <FilmMotif size={size} className={cls} />;
    case 'cathode':
      return <CathodeMotif size={size} className={cls} />;
    case 'kitchen':
      return <KitchenMotif size={size} className={cls} />;
    case 'book':
      return <BookMotif size={size} className={cls} />;
    case 'paw':
      return <PawMotif size={size} className={cls} />;
    case 'palette':
      return <PaletteMotif size={size} className={cls} />;
    case 'bookstack':
      return <BookStackMotif size={size} className={cls} />;
    case 'column':
      return <ColumnMotif size={size} className={cls} />;
    case 'flask':
      return <FlaskMotif size={size} className={cls} />;
    case 'pennant':
      return <PennantMotif size={size} className={cls} />;
  }
}

function VinylMotif({ size, className }: { size: number; className: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 84 84"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="42" cy="42" r="36" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1.4" />
      <circle cx="42" cy="42" r="28" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1" />
      <circle cx="42" cy="42" r="22" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1" />
      <circle cx="42" cy="42" r="16" stroke="currentColor" strokeOpacity="0.25" strokeWidth="1" />
      <circle cx="42" cy="42" r="10" fill="currentColor" fillOpacity="0.55" />
      <circle cx="42" cy="42" r="1.7" fill="var(--paper)" />
    </svg>
  );
}

function CassetteMotif({ size, className }: { size: number; className: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 84 84"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="8"
        y="22"
        width="68"
        height="40"
        rx="4"
        stroke="currentColor"
        strokeOpacity="0.6"
        strokeWidth="1.6"
      />
      <rect
        x="14"
        y="32"
        width="56"
        height="14"
        rx="2"
        stroke="currentColor"
        strokeOpacity="0.4"
        strokeWidth="1.2"
      />
      <circle cx="26" cy="39" r="4" fill="currentColor" fillOpacity="0.7" />
      <circle cx="58" cy="39" r="4" fill="currentColor" fillOpacity="0.7" />
      <line x1="20" y1="55" x2="64" y2="55" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1.2" />
    </svg>
  );
}

function AtlasMotif({ size, className }: { size: number; className: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 84 84"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="42" cy="42" r="32" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1.2" />
      <circle cx="42" cy="42" r="22" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1" />
      <path d="M42 8 L46 42 L42 76 L38 42 Z" fill="currentColor" fillOpacity="0.55" />
      <path d="M8 42 L42 38 L76 42 L42 46 Z" fill="currentColor" fillOpacity="0.55" />
      <path d="M18 18 L44 40 L66 66 L40 44 Z" fill="currentColor" fillOpacity="0.35" />
      <path d="M66 18 L44 40 L18 66 L40 44 Z" fill="currentColor" fillOpacity="0.35" />
      <circle cx="42" cy="42" r="3" fill="currentColor" fillOpacity="0.7" />
    </svg>
  );
}

function FilmMotif({ size, className }: { size: number; className: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 84 84"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="14"
        y="10"
        width="56"
        height="64"
        rx="2"
        stroke="currentColor"
        strokeOpacity="0.6"
        strokeWidth="1.8"
      />
      <rect
        x="20"
        y="18"
        width="44"
        height="48"
        stroke="currentColor"
        strokeOpacity="0.35"
        strokeWidth="1.2"
      />
      <circle cx="72" cy="20" r="1.8" fill="currentColor" fillOpacity="0.55" />
      <circle cx="72" cy="32" r="1.8" fill="currentColor" fillOpacity="0.55" />
      <circle cx="72" cy="44" r="1.8" fill="currentColor" fillOpacity="0.55" />
      <circle cx="72" cy="56" r="1.8" fill="currentColor" fillOpacity="0.55" />
      <circle cx="72" cy="68" r="1.8" fill="currentColor" fillOpacity="0.55" />
      <circle cx="42" cy="34" r="6" fill="currentColor" fillOpacity="0.55" />
      <path d="M30 56 Q42 44 54 56 L54 64 L30 64 Z" fill="currentColor" fillOpacity="0.55" />
    </svg>
  );
}

function CathodeMotif({ size, className }: { size: number; className: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 84 84"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="8"
        y="14"
        width="68"
        height="48"
        rx="6"
        stroke="currentColor"
        strokeOpacity="0.6"
        strokeWidth="1.8"
      />
      <rect
        x="16"
        y="22"
        width="52"
        height="32"
        rx="5"
        fill="currentColor"
        fillOpacity="0.55"
      />
      <line x1="20" y1="30" x2="64" y2="30" stroke="var(--paper)" strokeOpacity="0.3" strokeWidth="0.6" />
      <line x1="20" y1="36" x2="64" y2="36" stroke="var(--paper)" strokeOpacity="0.3" strokeWidth="0.6" />
      <line x1="20" y1="42" x2="64" y2="42" stroke="var(--paper)" strokeOpacity="0.3" strokeWidth="0.6" />
      <line x1="20" y1="48" x2="64" y2="48" stroke="var(--paper)" strokeOpacity="0.3" strokeWidth="0.6" />
      <line x1="22" y1="62" x2="22" y2="72" stroke="currentColor" strokeOpacity="0.6" strokeWidth="1.8" />
      <line x1="62" y1="62" x2="62" y2="72" stroke="currentColor" strokeOpacity="0.6" strokeWidth="1.8" />
      <circle cx="42" cy="58.5" r="1.6" fill="currentColor" fillOpacity="0.6" />
    </svg>
  );
}

function BookMotif({ size, className }: { size: number; className: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 84 84"
      fill="none"
      aria-hidden="true"
    >
      <path d="M10 24 L42 28 L42 60 L10 64 Z" fill="currentColor" fillOpacity="0.55" />
      <path d="M74 24 L42 28 L42 60 L74 64 Z" fill="currentColor" fillOpacity="0.55" />
      <line x1="42" y1="28" x2="42" y2="60" stroke="currentColor" strokeOpacity="0.7" strokeWidth="1.4" />
      <line x1="16" y1="34" x2="38" y2="35" stroke="var(--paper)" strokeOpacity="0.45" strokeWidth="0.9" />
      <line x1="16" y1="40" x2="38" y2="41" stroke="var(--paper)" strokeOpacity="0.45" strokeWidth="0.9" />
      <line x1="16" y1="46" x2="34" y2="47" stroke="var(--paper)" strokeOpacity="0.45" strokeWidth="0.9" />
      <line x1="16" y1="52" x2="38" y2="53" stroke="var(--paper)" strokeOpacity="0.45" strokeWidth="0.9" />
      <line x1="46" y1="35" x2="68" y2="34" stroke="var(--paper)" strokeOpacity="0.45" strokeWidth="0.9" />
      <line x1="46" y1="41" x2="68" y2="40" stroke="var(--paper)" strokeOpacity="0.45" strokeWidth="0.9" />
      <line x1="46" y1="47" x2="64" y2="46" stroke="var(--paper)" strokeOpacity="0.45" strokeWidth="0.9" />
      <line x1="46" y1="53" x2="68" y2="52" stroke="var(--paper)" strokeOpacity="0.45" strokeWidth="0.9" />
    </svg>
  );
}

function PawMotif({ size, className }: { size: number; className: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 84 84"
      fill="none"
      aria-hidden="true"
    >
      <ellipse cx="42" cy="52" rx="15" ry="12" fill="currentColor" fillOpacity="0.55" />
      <ellipse cx="24" cy="36" rx="6" ry="8" fill="currentColor" fillOpacity="0.5" />
      <ellipse cx="36" cy="26" rx="6" ry="8" fill="currentColor" fillOpacity="0.55" />
      <ellipse cx="50" cy="26" rx="6" ry="8" fill="currentColor" fillOpacity="0.55" />
      <ellipse cx="62" cy="36" rx="6" ry="8" fill="currentColor" fillOpacity="0.5" />
      <circle cx="42" cy="52" r="19" stroke="currentColor" strokeOpacity="0.25" strokeWidth="1" />
    </svg>
  );
}

function PaletteMotif({ size, className }: { size: number; className: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 84 84"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M42 12 C24 12 10 25 10 41 C10 57 24 70 42 70 C47 70 50 67 50 63 C50 60 48 59 48 56 C48 52 51 50 55 50 L63 50 C69 50 74 45 74 39 C74 24 60 12 42 12 Z"
        stroke="currentColor"
        strokeOpacity="0.6"
        strokeWidth="1.6"
      />
      <circle cx="28" cy="30" r="4.5" fill="currentColor" fillOpacity="0.6" />
      <circle cx="44" cy="24" r="4.5" fill="currentColor" fillOpacity="0.45" />
      <circle cx="58" cy="32" r="4.5" fill="currentColor" fillOpacity="0.55" />
      <circle cx="24" cy="46" r="4.5" fill="currentColor" fillOpacity="0.35" />
      <line x1="60" y1="64" x2="74" y2="70" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.6" strokeLinecap="round" />
      <line x1="54" y1="60" x2="62" y2="65" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function BookStackMotif({ size, className }: { size: number; className: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 84 84"
      fill="none"
      aria-hidden="true"
    >
      <rect x="16" y="54" width="52" height="11" rx="1.5" fill="currentColor" fillOpacity="0.6" />
      <rect x="22" y="42" width="44" height="11" rx="1.5" fill="currentColor" fillOpacity="0.45" />
      <rect x="18" y="30" width="48" height="11" rx="1.5" fill="currentColor" fillOpacity="0.55" />
      <line x1="24" y1="59.5" x2="60" y2="59.5" stroke="var(--paper)" strokeOpacity="0.4" strokeWidth="0.9" />
      <line x1="30" y1="47.5" x2="58" y2="47.5" stroke="var(--paper)" strokeOpacity="0.4" strokeWidth="0.9" />
      <line x1="26" y1="35.5" x2="58" y2="35.5" stroke="var(--paper)" strokeOpacity="0.4" strokeWidth="0.9" />
      <path
        d="M34 30 L34 16 Q42 20 50 16 L50 30"
        stroke="currentColor"
        strokeOpacity="0.4"
        strokeWidth="1.4"
        fill="none"
      />
    </svg>
  );
}

function ColumnMotif({ size, className }: { size: number; className: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 84 84"
      fill="none"
      aria-hidden="true"
    >
      <path d="M14 22 L42 10 L70 22 Z" stroke="currentColor" strokeOpacity="0.6" strokeWidth="1.6" fill="currentColor" fillOpacity="0.25" />
      <rect x="16" y="24" width="52" height="5" rx="1" fill="currentColor" fillOpacity="0.55" />
      <line x1="24" y1="32" x2="24" y2="60" stroke="currentColor" strokeOpacity="0.55" strokeWidth="3.4" />
      <line x1="36" y1="32" x2="36" y2="60" stroke="currentColor" strokeOpacity="0.45" strokeWidth="3.4" />
      <line x1="48" y1="32" x2="48" y2="60" stroke="currentColor" strokeOpacity="0.45" strokeWidth="3.4" />
      <line x1="60" y1="32" x2="60" y2="60" stroke="currentColor" strokeOpacity="0.55" strokeWidth="3.4" />
      <rect x="16" y="62" width="52" height="5" rx="1" fill="currentColor" fillOpacity="0.55" />
      <rect x="12" y="69" width="60" height="5" rx="1" fill="currentColor" fillOpacity="0.6" />
    </svg>
  );
}

function FlaskMotif({ size, className }: { size: number; className: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 84 84"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M36 12 L36 34 L20 62 Q17 68 23 68 L61 68 Q67 68 64 62 L48 34 L48 12"
        stroke="currentColor"
        strokeOpacity="0.6"
        strokeWidth="1.6"
        fill="none"
      />
      <line x1="32" y1="12" x2="52" y2="12" stroke="currentColor" strokeOpacity="0.6" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M27 50 L57 50 L64 62 Q67 68 61 68 L23 68 Q17 68 20 62 Z" fill="currentColor" fillOpacity="0.5" />
      <circle cx="38" cy="44" r="2" fill="currentColor" fillOpacity="0.45" />
      <circle cx="46" cy="38" r="1.6" fill="currentColor" fillOpacity="0.35" />
      <circle cx="42" cy="30" r="1.3" fill="currentColor" fillOpacity="0.3" />
    </svg>
  );
}

function PennantMotif({ size, className }: { size: number; className: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 84 84"
      fill="none"
      aria-hidden="true"
    >
      <line x1="20" y1="12" x2="20" y2="72" stroke="currentColor" strokeOpacity="0.6" strokeWidth="2" strokeLinecap="round" />
      <path d="M22 16 L70 26 L22 40 Z" fill="currentColor" fillOpacity="0.55" />
      <path d="M22 16 L70 26 L22 40" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.2" />
      <circle cx="20" cy="12" r="2.4" fill="currentColor" fillOpacity="0.7" />
      <circle cx="52" cy="58" r="10" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.4" />
      <path
        d="M45 51 Q52 58 59 65 M59 51 Q52 58 45 65"
        stroke="currentColor"
        strokeOpacity="0.35"
        strokeWidth="1"
      />
    </svg>
  );
}

function KitchenMotif({ size, className }: { size: number; className: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 84 84"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M30 14 Q34 20 30 26 Q26 32 30 38"
        stroke="currentColor"
        strokeOpacity="0.4"
        strokeWidth="1.4"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M42 10 Q46 16 42 22 Q38 28 42 34"
        stroke="currentColor"
        strokeOpacity="0.45"
        strokeWidth="1.4"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M54 14 Q58 20 54 26 Q50 32 54 38"
        stroke="currentColor"
        strokeOpacity="0.4"
        strokeWidth="1.4"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M14 50 Q18 46 22 50 Q26 46 30 50 Q34 46 38 50 Q42 46 46 50 Q50 46 54 50 Q58 46 62 50 Q66 46 70 50 L70 56 L14 56 Z"
        fill="currentColor"
        fillOpacity="0.55"
      />
      <path
        d="M10 56 L74 56 L66 70 L18 70 Z"
        fill="currentColor"
        fillOpacity="0.7"
      />
      <line x1="32" y1="58" x2="38" y2="68" stroke="var(--paper)" strokeOpacity="0.4" strokeWidth="0.9" />
      <line x1="46" y1="58" x2="52" y2="68" stroke="var(--paper)" strokeOpacity="0.4" strokeWidth="0.9" />
    </svg>
  );
}
